import { ICustomersHook } from '@models';
import { CustomerService } from '@services';
import { useStore } from '@stores';
import { debounce } from 'lodash';
import { useCallback, useEffect, useState } from 'react';

export const useCustomers = (): ICustomersHook => {
  const { customerStore } = useStore();
  const {
    customers,
    totalCount,
    loading,
    currentPage,
    skipCount,
    maxResultCount,
    setSkipCount,
    setCurrentPage,
  } = customerStore;
  const [fullname, setFullname] = useState<string | null>('');
  const [fullnameInput, setFullnameInput] = useState('');
  const [email, setEmail] = useState<string | null>('');
  const [emailInput, setEmailInput] = useState('');
  const [isAppUser, setIsAppUser] = useState<boolean | undefined>(undefined);

  useEffect(() => {
    return () => customerStore.resetCustomers();
  }, [customerStore]);

  useEffect(() => {
    customerStore.getAll({
      fullname,
      email,
      maxResultCount,
      skipCount,
      isAppUser,
    });
  }, [customerStore, isAppUser, maxResultCount, skipCount, fullname, email]);

  const handlePageChange = (pagination: any) => {
    customerStore.setMaxResultCount(pagination.pageSize);
    customerStore.setSkipCount((pagination.current - 1) * pagination.pageSize);
    customerStore.setCurrentPage(pagination.current);
  };

  const handleFullnameSearch = debounce((text: string) => {
    setFullname(text);
    setSkipCount(0);
    setCurrentPage(1);
  }, 500);

  const handleFullnameInputChange = (text: string) => {
    setFullnameInput(text);
    handleFullnameSearch(text);
  };

  const handleEmailSearch = debounce((text: string) => {
    setEmail(text);
    setSkipCount(0);
    setCurrentPage(1);
  }, 500);

  const handleEmailInputChange = (text: string) => {
    setEmailInput(text);
    handleEmailSearch(text);
  };

  const handleIsAppUserChange = (value: boolean | undefined) => {
    setIsAppUser(value);
    setSkipCount(0);
    setCurrentPage(1);
  };

  const refresh = () => {
    customerStore.getAll({
      fullname,
      email,
      maxResultCount,
      skipCount,
      isAppUser,
    });
  };

  const fetchAllForExport = useCallback(async () => {
    const result = await CustomerService.getAll({
      fullname,
      email,
      isAppUser,
      maxResultCount: totalCount || 10000,
      skipCount: 0,
    });
    return result.items;
  }, [fullname, email, isAppUser, totalCount]);

  return {
    loading,
    dataSource: Array.from(customers.values()),
    totalCount,
    currentPage,
    maxResultCount,
    fullnameInput,
    emailInput,
    isAppUser,
    handleEmailSearch: handleEmailInputChange,
    handleFullnameSearch: handleFullnameInputChange,
    handlePageChange,
    handleIsAppUserChange,
    refresh,
    fetchAllForExport,
  };
};
